import { useEffect, useState, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Header from '../components/layout/header';
import Sidebar from '../components/layout/sidebar';
import Footer from '../components/layout/footer';
import {
  getMainWallets,
  getCryptoWallets,
  getMasterCardData,
  getMasterCardBalance,
  updateAssetsStatus,
  getZenusAccount
} from '../store/assetsReducer';
import { getTicketStatus } from '../store/authReducer';
import { getExchangeRates } from '../store/settingReducer';
import type { AppDispatch, RootState } from '../store';
import { clearAllWalletCache } from '../utils/clear-cache';
import { RefreshCw } from 'lucide-react';
import { DashboardProvider } from '../context/DashboardContext';

interface WalletData {
  mainWallets: { status: string };
  cryptoWallets: { status: string };
  masterCard: { data: Array<{ id: string }> };
}

export default function Dashboard() {
  const { updateUserPhoto, user } = useAuth();
  const dispatch = useDispatch<AppDispatch>();
  const { data: { cryptoPrices, fiatPrices }, status } = useSelector((state: RootState) => state.setting);
  const { data: { mainWallets, cryptoWallets, masterCard } } = useSelector((state: RootState) => state.assets) as { data: WalletData };
  const [showSidebar, setShowSidebar] = useState(false);
  const [isLoadingDashboard, setIsLoadingDashboard] = useState(true);
  const hasRun = useRef(false);
  const pricesIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const balancesIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const toggleSidebar = useCallback(() => {
    setShowSidebar(prev => !prev);
  }, []);

  // Función para cargar datos secuencialmente
  const loadDashboardData = useCallback(async () => {
    try {
      setIsLoadingDashboard(true);

      // FASE 1: Cargar Fiat Wallets (Zenus USD)
      await dispatch(getZenusAccount()).unwrap();

      // FASE 2: Cargar MasterCard (solo si es INDIVIDUAL)
      const isIndividual = user?.accountStatus === 'onboarding_individual_complete' ||
                           user?.currentPhase === 'ONBOARDING_COMPLETE';
      if (isIndividual) {
        const cardData = await dispatch(getMasterCardData()).unwrap();
        if (cardData.success && cardData.data.data[0]?.id) {
          const balanceData = {
            card: cardData.data.data[0].id,
            fiatPrices
          };
          await dispatch(getMasterCardBalance(balanceData)).unwrap();
        }
      }

      // FASE 3: Cargar Crypto Wallets
      await dispatch(getCryptoWallets(cryptoPrices)).unwrap();

      // FASE 4: Cargar Main Wallets (legacy)
      const walletData = {
        virtual: user.virtualDash,
        cryptoPrices,
        fiatPrices
      };
      await dispatch(getMainWallets(walletData)).unwrap();

      dispatch(updateAssetsStatus());
    } catch (error) {
      console.error('Dashboard load failed:', error);
    } finally {
      setIsLoadingDashboard(false);
    }
  }, [dispatch, user?.currentPhase, user?.virtualDash, cryptoPrices, fiatPrices]);

  const init = useCallback(async () => {
    if (status !== 'succeeded' || hasRun.current) return;
    hasRun.current = true;

    // Limpiar caché
    clearAllWalletCache();

    // Cargar tickets de soporte
    dispatch(getTicketStatus());

    // Cargar datos del dashboard
    await loadDashboardData();

    // CONFIGURAR AUTO-REFRESH
    // Actualizar precios cada 10 minutos
    pricesIntervalRef.current = setInterval(() => {
      dispatch(getExchangeRates());
    }, 10 * 60 * 1000);

    // Actualizar saldos cada 5 minutos
    balancesIntervalRef.current = setInterval(() => {
      loadDashboardData();
    }, 5 * 60 * 1000);
  }, [dispatch, status, loadDashboardData]);

  useEffect(() => {
    if (!hasRun.current && status === 'succeeded') {
      init();
    }
  }, [init, status]);

  // Cleanup de intervals al desmontar
  useEffect(() => {
    return () => {
      if (pricesIntervalRef.current) {
        clearInterval(pricesIntervalRef.current);
      }
      if (balancesIntervalRef.current) {
        clearInterval(balancesIntervalRef.current);
      }
    };
  }, []);

  // Pantalla de carga mientras se obtienen datos
  if (isLoadingDashboard) {
    return (
      <div className="flex flex-col min-h-screen bg-gradient-to-b from-background-gold via-background-gold-transparent to-background-silver">
        <Header updateUserPhoto={updateUserPhoto} toggleSidebar={toggleSidebar} />
        <div className="flex flex-1 items-center justify-center">
          <div className="text-center">
            <RefreshCw className="animate-spin mx-auto mb-4" size={48} color="#B3A369" />
            <p className="text-lg text-gray-600 font-medium">Loading your dashboard...</p>
            <p className="text-sm text-gray-500 mt-2">Please wait while we fetch your data</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <DashboardProvider refreshDashboard={loadDashboardData}>
      <div className="flex flex-col min-h-screen bg-gradient-to-b from-[#003DA5] to-[#1E63D9]">
        <Header updateUserPhoto={updateUserPhoto} toggleSidebar={toggleSidebar} />
        <div className="flex flex-1 overflow-hidden">
          <div className="hidden sm:block sm:w-1/5 min-w-[275px] border-r border-black/10">
            <Sidebar />
          </div>
          {showSidebar && (
            <div className="fixed inset-0 z-50 w-full sm:hidden overflow-y-auto bg-white/95">
              <Sidebar toggleSidebar={toggleSidebar} />
            </div>
          )}
          <div className={`flex-1 overflow-y-auto p-4 sm:p-6 transition-opacity duration-200 ${showSidebar ? 'opacity-0 sm:opacity-100' : 'opacity-100'}`}>
            <Outlet />
          </div>
        </div>
        <Footer />
      </div>
    </DashboardProvider>
  );
}